/*
  Glow on Tee — cart bridge between our own code and Horizon's cart drawer.

  Replaces the old sections/gt-cart-drawer.liquid. Horizon now owns the
  drawer itself (snippets/cart-drawer.liquid); this module connects our own
  pieces to it:

  1. window.GTCart.add(items, { source }) — used by main-product and
     gt-bundle-save. Posts to /cart/add.js and announces the change with the
     standard "shopify:cart:lines-update" event, including the drawer's
     section HTML, so Horizon re-renders the drawer exactly as it does for
     its own add-to-cart forms.
  2. Fly-to-cart — every successful add, from any source (our forms,
     Bundle & Save, Horizon quick add), flies the product image to the
     header cart icon and then opens the drawer. Same animation, timing and
     image choice as the old drawer. One add, one animation (900 ms guard).
  3. Header cart link — a click on a /cart link opens the drawer instead of
     navigating, but only once the drawer is confirmed present. If the drawer
     did not render, the browser goes to /cart as normal.
  4. Header badge — the count in [data-gt-cart-link] follows the cart.

  The drawer's own "auto open" setting is off on purpose: opening is done
  here, after the animation, so the image lands before the drawer slides in.
*/

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const CART_PATH = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
  ? window.Shopify.routes.root.replace(/\/$/, '')
  : '') + '/cart';

let lastClick = null;
let lastAdd = 0;

function drawer() {
  return document.querySelector('theme-drawer#cart-drawer');
}

function openDrawer() {
  const d = drawer();
  if (d && typeof d.open === 'function') d.open();
}

function sectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId && ids.indexOf(el.dataset.sectionId) === -1) {
      ids.push(el.dataset.sectionId);
    }
  });
  return ids;
}

async function fetchCart() {
  const response = await fetch(CART_PATH + '.js', { headers: { Accept: 'application/json' } });
  return response.json();
}

/**
 * Add one or more lines.
 * @param {Array<{id: number|string, quantity?: number, properties?: object}>|FormData} items
 * @param {{source?: Element}} [options]
 * @returns {Promise<object>} the /cart/add.js response
 */
async function add(items, options = {}) {
  const source = options.source instanceof Element ? options.source : document;
  const ids = sectionIds();

  let body;
  let headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
  let lines;

  if (items instanceof FormData) {
    body = items;
    if (ids.length) {
      body.set('sections', ids.join(','));
      body.set('sections_url', window.location.pathname);
    }
    lines = [{ merchandiseId: String(body.get('id')), quantity: Number(body.get('quantity')) || 1 }];
  } else {
    const payload = { items: items.map((i) => ({ ...i, quantity: i.quantity || 1 })) };
    if (ids.length) {
      payload.sections = ids.join(',');
      payload.sections_url = window.location.pathname;
    }
    body = JSON.stringify(payload);
    headers['Content-Type'] = 'application/json';
    lines = payload.items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity }));
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines,
      promise: deferred.promise,
    })
  );

  let response;
  try {
    const r = await fetch(CART_PATH + '/add.js', { method: 'POST', headers, body });
    response = await r.json();
    if (!r.ok || response.status) {
      const ajaxCart = await fetchCart().catch(() => null);
      deferred.resolve({
        cart: ajaxCart ? CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart) : null,
        detail: { didError: true, source: 'gt-cart-bridge' },
      });
      const error = new Error(response.description || response.message || 'Add to cart failed');
      error.response = response;
      throw error;
    }
  } catch (error) {
    if (!error.response) deferred.reject(error);
    throw error;
  }

  const ajaxCart = await fetchCart();
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
    detail: {
      items: ajaxCart.items,
      source: 'gt-cart-bridge',
      sections: response.sections,
      didError: false,
    },
  });
  return response;
}

/* ---------- header badge ---------- */

function updateBadge(count) {
  document.querySelectorAll('[data-gt-cart-link]').forEach((link) => {
    let badge = link.querySelector('.gt-badge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'gt-badge';
        link.appendChild(badge);
      }
      badge.textContent = String(count);
    } else if (badge) {
      badge.remove();
    }
    link.setAttribute('aria-label', 'Cart, ' + count + ' items');
  });
}

/* ---------- fly to cart (ported from the old gt-cart-drawer) ---------- */

function reducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function cartIconEl() {
  const marked = document.querySelectorAll('[data-gt-cart-link]');
  for (const link of marked) {
    const rect = link.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return link;
  }
  const links = document.querySelectorAll('a[href]');
  for (const link of links) {
    if (link.closest('#cart-drawer')) continue;
    let path;
    try {
      path = new URL(link.href, window.location.origin).pathname;
    } catch (e) {
      continue;
    }
    if (path !== CART_PATH) continue;
    const rect = link.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return link;
  }
  return null;
}

// For a Bundle & Save row: the row's own thumbnail. For a product card:
// the card's image. Otherwise the largest visible image in the main
// content, which on a product page is the gallery image being viewed.
function sourceImage() {
  if (lastClick && lastClick.closest) {
    const row = lastClick.closest('[data-gt-row]');
    if (row) {
      const thumb = row.querySelector('img');
      if (thumb) return thumb;
    }
    const card = lastClick.closest('product-card, .gmd-card');
    if (card) {
      const img = card.querySelector('img');
      if (img && img.getBoundingClientRect().width > 0) return img;
    }
  }
  const scope = document.getElementById('MainContent') || document.body;
  let best = null;
  let bestArea = 0;
  scope.querySelectorAll('img').forEach((img) => {
    const rect = img.getBoundingClientRect();
    if (rect.width < 120 || rect.height < 120) return;
    if (rect.bottom < 0 || rect.top > window.innerHeight) return;
    const area = rect.width * rect.height;
    if (area > bestArea) {
      bestArea = area;
      best = img;
    }
  });
  return best;
}

function fly(done) {
  if (reducedMotion()) return done();

  const image = sourceImage();
  const target = cartIconEl();
  if (!image || !target || typeof image.animate !== 'function') return done();

  const from = image.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  if (!from.width || !to.width) return done();

  const clone = document.createElement('img');
  clone.src = image.currentSrc || image.src;
  clone.alt = '';
  clone.setAttribute('aria-hidden', 'true');
  clone.className = 'gtc-fly';
  clone.style.left = from.left + 'px';
  clone.style.top = from.top + 'px';
  clone.style.width = from.width + 'px';
  clone.style.height = from.height + 'px';
  document.body.appendChild(clone);

  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);

  let finished = false;
  function end() {
    if (finished) return;
    finished = true;
    if (clone.parentNode) clone.parentNode.removeChild(clone);
    done();
  }

  try {
    const animation = clone.animate(
      [
        { transform: 'translate(0px, 0px) scale(1)', opacity: 1 },
        {
          transform: 'translate(' + dx * 0.55 + 'px, ' + (dy * 0.45 - 40) + 'px) scale(0.5)',
          opacity: 0.85,
          offset: 0.6,
        },
        { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(0.12)', opacity: 0.1 },
      ],
      { duration: 620, easing: 'cubic-bezier(.4, 0, .2, 1)', fill: 'forwards' }
    );
    animation.onfinish = end;
    animation.oncancel = end;
  } catch (e) {
    return end();
  }

  window.setTimeout(end, 900);
}

// Single funnel for every successful add.
function added(sourceModal) {
  const now = Date.now();
  if (now - lastAdd < 900) return;
  lastAdd = now;

  if (sourceModal && sourceModal.open) {
    // Quick add: let the modal close (and restore focus) first.
    sourceModal.addEventListener('close', () => fly(openDrawer), { once: true });
    return;
  }
  fly(openDrawer);
}

/* ---------- events ---------- */

document.addEventListener('click', (event) => {
  lastClick = event.target;
}, true);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
  const isAdd = event.action === 'add';
  if (!event.promise) return;
  event.promise
    .then((result) => {
      const detail = result && result.detail;
      const items = detail && detail.items;
      if (Array.isArray(items)) {
        updateBadge(items.reduce((n, i) => n + (i.quantity || 0), 0));
      } else {
        fetchCart().then((c) => updateBadge(c.item_count)).catch(() => {});
      }
      if (isAdd && !(detail && detail.didError)) added(sourceModal);
    })
    .catch(() => {});
});

// Header cart link opens the drawer (fail-safe: only when the drawer exists).
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!target || !target.closest || event.defaultPrevented) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
  const link = target.closest('a[href]');
  if (!link || link.closest('#cart-drawer')) return;
  let path;
  try {
    path = new URL(link.href, window.location.origin).pathname;
  } catch (e) {
    return;
  }
  if (path !== CART_PATH) return;
  const d = drawer();
  if (!d || typeof d.open !== 'function') return;
  event.preventDefault();
  d.open();
});

// Our own product forms (main-product, its family-set rows) post to
// /cart/add; they are sent through the bridge instead. Horizon's own forms
// sit inside <product-form-component> and handle themselves, so they are
// skipped. On failure the shopper lands on /cart, as before.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
  if (form.closest('product-form-component')) return;
  const action = form.getAttribute('action') || '';
  if (action.indexOf('/cart/add') === -1) return;
  if (!drawer()) return;
  event.preventDefault();
  const submitter = event.submitter || null;
  const data = submitter && submitter.name ? new FormData(form, submitter) : new FormData(form);
  add(data, { source: form }).catch(() => {
    window.location.href = CART_PATH;
  });
});

window.GTCart = { add, open: openDrawer, refreshBadge: () => fetchCart().then((c) => updateBadge(c.item_count)) };
document.dispatchEvent(new CustomEvent('gt:cart-bridge-ready'));
